/*
 * Réactions qui affinent un pouce, comme dans l'app : un appui long (ou le survol à la souris)
 * sur un pouce ouvre cinq emojis. La réaction part dans la note existante, en `rating_message`,
 * toujours avec son libellé français (« 😍 J'adore »), pour que le Studio les compte pareil
 * quelle que soit la langue du lecteur.
 */

export interface Reaction {
  emoji: string;
  /** Libellé envoyé au serveur avec l'emoji. */
  fr: string;
  en: string;
}

export const positiveReactions: readonly Reaction[] = [
  { emoji: "😍", fr: "J'adore", en: "Love it" },
  { emoji: "😂", fr: "Ça m'a fait rire", en: "Made me laugh" },
  { emoji: "🤯", fr: "Bluffant", en: "Mind-blowing" },
  { emoji: "💡", fr: "J'ai appris quelque chose", en: "I learned something" },
  { emoji: "🙏", fr: "Merci", en: "Thank you" },
];

export const negativeReactions: readonly Reaction[] = [
  { emoji: "🤔", fr: "Pas clair", en: "Unclear" },
  { emoji: "❌", fr: "C'est faux", en: "That's wrong" },
  { emoji: "🙈", fr: "Ça spoile la suite", en: "Spoils what's next" },
  { emoji: "😴", fr: "Trop long", en: "Too long" },
  { emoji: "🙄", fr: "À côté de la question", en: "Missed the question" },
];

/** Entre une réaction et le commentaire qui la précise : « 😴 Trop long — la fin suffisait ». */
const DETAIL_SEPARATOR = " — ";

/** Texte de `rating_message` pour une réaction, suivie du commentaire qui la précise s'il y en a un. */
export const reactionMessage = (reaction: Reaction, detail?: string | null): string =>
  `${reaction.emoji} ${reaction.fr}${detail?.trim() ? `${DETAIL_SEPARATOR}${detail.trim()}` : ""}`;

/**
 * Réaction portée par une note : son message est exactement l'une des réactions, comme dans l'app,
 * ou une réaction suivie de son commentaire.
 */
export function reactionOf(rating: number | null, ratingMessage: string | null): Reaction | null {
  if (rating == null || !ratingMessage) return null;
  const list = rating === 1 ? positiveReactions : negativeReactions;
  return (
    list.find((r) => {
      const base = reactionMessage(r);
      return ratingMessage.startsWith(base);
    }) ?? null
  );
}

/** Commentaire écrit après une réaction, "" s'il n'y en a pas. */
export function reactionDetail(reaction: Reaction, ratingMessage: string | null): string {
  return (ratingMessage ?? "").slice(reactionMessage(reaction).length).replace(/^\s*[—:-]?\s*/, "").trim();
}
